import React, { useState, useEffect } from 'react';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import { Grid, Autocomplete, CircularProgress, Divider, Icon } from '@mui/material';
import { useMaterialUIController } from 'context';
import { get, postNoResponse } from 'utils/WebService';

const SituacaoEnum = [
    { code: 1, label: 'Devolvido' },
    { code: 2, label: 'Cobrança' },
    { code: 3, label: 'Negociação' },
    { code: 4, label: 'Pesquisa' },
    { code: 5, label: 'Suspenso' },
    { code: 6, label: 'Migrado' },
    { code: 7, label: 'Atualização de Cadastro' },
    { code: 8, label: 'Ocorrência do Usuário' },
    { code: 9, label: 'Importação' }
];

export default function MigracaoDeParaDominios({ showSnackbar, idCliente }) {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    
    // Legacy Domains
    const [legacySituacoes, setLegacySituacoes] = useState([]);
    const [legacyCobradores, setLegacyCobradores] = useState([]);
    const [legacyStatus, setLegacyStatus] = useState([]);
    const [legacyClientes, setLegacyClientes] = useState([]);
    
    // New Domains options
    const [usuarios, setUsuarios] = useState([]);
    const [statusFicha, setStatusFicha] = useState([]);
    const [clientesRal, setClientesRal] = useState([]);
    
    // Mappings (key = legacy value, value = new ID)
    const [mapSituacao, setMapSituacao] = useState({});
    const [mapCobrador, setMapCobrador] = useState({});
    const [mapStatus, setMapStatus] = useState({});
    const [mapCliente, setMapCliente] = useState({});

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => {
            fetchLegacyDomains();
        }, 500);
        return () => clearTimeout(timer);
    }, [idCliente]);

    const fetchLegacyDomains = async () => {
        setLoading(true);
        try {
            const endpoint = idCliente && idCliente.trim() !== '' 
                ? `/migracao/legacy-domains?idClienteLegado=${idCliente.trim()}`
                : '/migracao/legacy-domains';
            const resLegacy = await get(endpoint);
            setLegacySituacoes(resLegacy?.situacao || []);
            setLegacyCobradores(resLegacy?.cobrador || []);
            setLegacyStatus(resLegacy?.status || []);
            setLegacyClientes(resLegacy?.cliente || []);
        } catch (err) {
            showSnackbar('Erro ao carregar domínios legados: ' + err.message, false);
        } finally {
            setLoading(false);
        }
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            // Fetch current DB options
            const resUsers = await get('/users/list-all-including-inactive');
            const usersArray = resUsers?.list || resUsers?.data || (Array.isArray(resUsers) ? resUsers : []);
            setUsuarios(usersArray);
            
            const resStatus = await get('/status-ficha/list-all');
            const statusArray = resStatus?.list || resStatus?.data || (Array.isArray(resStatus) ? resStatus : []);
            setStatusFicha(statusArray);

            const resClientesRal = await get('/migracao/clientes-ral');
            const clientesArray = Array.isArray(resClientesRal) ? resClientesRal : (resClientesRal?.list || resClientesRal?.data || []);
            setClientesRal(clientesArray);

            // Fetch existing mappings
            const resMappings = await get('/migracao/de-para-dominios');
            if (resMappings) {
                setMapSituacao(resMappings.situacao || {});
                setMapCobrador(resMappings.cobrador || {});
                setMapStatus(resMappings.status || {});
                setMapCliente(resMappings.cliente || {});
            }
        } catch (err) {
            showSnackbar('Erro ao carregar domínios: ' + err.message, false);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        // Validation for mandatory fields
        for (const sit of legacySituacoes) {
            if (!mapSituacao[sit.id]) {
                showSnackbar(`Mapeamento obrigatório: Preencha a correspondência para a Situação "${sit.label}"`, false);
                return;
            }
        }
        for (const stat of legacyStatus) {
            if (!mapStatus[stat.id]) {
                showSnackbar(`Mapeamento obrigatório: Preencha a correspondência para o Status "${stat.label}"`, false);
                return;
            }
        }

        setSaving(true);
        try {
            const payload = {
                situacao: mapSituacao,
                cobrador: mapCobrador,
                status: mapStatus,
                cliente: mapCliente
            };
            await postNoResponse('/migracao/de-para-dominios', payload);
            showSnackbar('De-Para salvo com sucesso!', true);
        } catch (err) {
            showSnackbar('Erro ao salvar De-Para: ' + err.message, false);
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <MDBox display="flex" justifyContent="center" p={3}>
                <CircularProgress color="info" />
            </MDBox>
        );
    }

    const renderMappingBlock = (title, legacyList, options, mapState, setMapState, optionLabel, optionValue, required = true) => {
        if (legacyList.length === 0) return null;

        return (
            <MDBox mb={3} p={2} sx={{ backgroundColor: darkMode ? "background.default" : "#f8f9fa", border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
                <MDTypography variant="h6" mb={2} color={darkMode ? "white" : "dark"}>
                    {title} {required && <span style={{color: 'red'}}>*</span>}
                </MDTypography>
                <Grid container spacing={2}>
                    {legacyList.map((legacyVal, idx) => (
                        <Grid item xs={12} md={6} key={idx}>
                            <MDBox display="flex" alignItems="center" gap={2}>
                                <MDBox flex={1}>
                                    <MDTypography variant="button" fontWeight="medium">
                                        De: {legacyVal.label}
                                    </MDTypography>
                                </MDBox>
                                <Icon>arrow_forward</Icon>
                                <MDBox flex={2}>
                                    <Autocomplete
                                        options={options}
                                        getOptionLabel={(option) => optionLabel(option)}
                                        value={options.find(o => String(optionValue(o)) === String(mapState[legacyVal.id])) || null}
                                        onChange={(e, newVal) => {
                                            setMapState(prev => ({
                                                ...prev,
                                                [legacyVal.id]: newVal ? String(optionValue(newVal)) : ''
                                            }));
                                        }}
                                        renderInput={(params) => <MDInput {...params} label="Para (Novo)" />}
                                        fullWidth
                                    />
                                </MDBox>
                            </MDBox>
                        </Grid>
                    ))}
                </Grid>
            </MDBox>
        );
    };

    return (
        <MDBox>
            <MDTypography variant="button" color="text" mb={2} display="block">
                Mapeie os valores do sistema legado para as opções correspondentes no novo sistema.
                <br/>* Situação e Status são obrigatórios. Cobrador é opcional.
            </MDTypography>

            {renderMappingBlock(
                "Mapeamento de Situação da Ficha", 
                legacySituacoes, 
                SituacaoEnum, 
                mapSituacao, setMapSituacao, 
                (opt) => `${opt.code} - ${opt.label}`, 
                (opt) => opt.code,
                true
            )}

            {renderMappingBlock(
                "Mapeamento de Status da Ficha", 
                legacyStatus, 
                statusFicha, 
                mapStatus, setMapStatus, 
                (opt) => opt.ccNome, 
                (opt) => opt.id, // Usually webservice returns id or cvId
                true
            )}

            {renderMappingBlock(
                "Mapeamento de Cobrador (Usuário)", 
                legacyCobradores, 
                usuarios, 
                mapCobrador, setMapCobrador, 
                (opt) => `${opt.nome || opt.ccNome} (${opt.email || opt.ccEmail || ''})`.replace(' ()', ''), 
                (opt) => opt.id || opt.cvCodUsuario, // Check how id is returned
                false
            )}

            {renderMappingBlock(
                "Mapeamento de Clientes (Opcional - Redirecionar para Cliente existente no RAL)", 
                legacyClientes, 
                clientesRal, 
                mapCliente, setMapCliente, 
                (opt) => `${opt.id} - ${opt.nome || opt.nomeComum || opt.nomeCompleto || ''} ${opt.documento ? '(' + opt.documento + ')' : ''}`, 
                (opt) => opt.id,
                false
            )}

            <MDBox display="flex" justifyContent="flex-end" mt={2}>
                <MDButton variant="gradient" color="success" disabled={saving} onClick={handleSave}>
                    {saving ? <CircularProgress size={20} color="white" /> : "Salvar Mapeamentos (De-Para)"}
                </MDButton>
            </MDBox>
        </MDBox>
    );
}
